import clsx from 'clsx';
import height from 'dom-helpers/height';
import querySelectorAll from 'dom-helpers/querySelectorAll';
import React, { Component, createRef } from 'react';
import BackgroundCells from 'react-big-calendar/lib/BackgroundCells';
import NoopWrapper from 'react-big-calendar/lib/NoopWrapper';
import ScrollableWeekWrapper from 'react-big-calendar/lib/ScrollableWeekWrapper';
import * as DateSlotMetrics from 'react-big-calendar/lib/utils/DateSlotMetrics';
import GapAwareEventEndingRow from './GapAwareEventEndingRow';
import GapAwareEventRow from './GapAwareEventRow';

export default class GapAwareDateContentRow extends Component {
  static defaultProps = {
    minRows: 0,
    maxRows: Infinity,
  };

  constructor(...args) {
    super(...args);
    this.containerRef = createRef();
    this.headingRowRef = createRef();
    this.eventRowRef = createRef();
    this.slotMetrics = DateSlotMetrics.getSlotMetrics();

    this.handleSelectSlot = (slot) => {
      const { range, onSelectSlot } = this.props;
      onSelectSlot(range.slice(slot.start, slot.end + 1), slot);
    };

    this.handleShowMore = (slot, target) => {
      const { range, onShowMore } = this.props;
      const metrics = this.slotMetrics(this.props);
      const row = querySelectorAll(this.containerRef.current, '.rbc-row-bg')[0];
      let cell;
      if (row) cell = row.children[slot - 1];
      const events = metrics.getEventsForSlot(slot);
      onShowMore(events, range[slot - 1], cell, slot, target);
    };

    this.getContainer = () => {
      const { container } = this.props;
      return container ? container() : this.containerRef.current;
    };

    this.renderHeadingCell = (date, index) => {
      const { renderHeader, getNow, localizer } = this.props;
      return renderHeader({
        date,
        key: `header_${index}`,
        className: clsx(
          'rbc-date-cell',
          localizer.isSameDate(date, getNow()) && 'rbc-now'
        ),
      });
    };

    this.renderDummy = () => {
      const { className, range, renderHeader, showAllEvents } = this.props;
      return (
        <div className={className} ref={this.containerRef}>
          <div
            className={clsx(
              'rbc-row-content',
              showAllEvents && 'rbc-row-content-scrollable'
            )}
          >
            {renderHeader && (
              <div className="rbc-row" ref={this.headingRowRef}>
                {range.map(this.renderHeadingCell)}
              </div>
            )}
            <div className="rbc-row" ref={this.eventRowRef}>
              <div className="rbc-row-segment">
                <div className="rbc-event">
                  <div className="rbc-event-content">&nbsp;</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };
  }

  getRowLimit() {
    const eventHeight = height(this.eventRowRef.current);
    const headingHeight = this.headingRowRef.current
      ? height(this.headingRowRef.current)
      : 0;
    const eventSpace = height(this.containerRef.current) - headingHeight;
    return Math.max(Math.floor(eventSpace / eventHeight), 1);
  }

  render() {
    const {
      date,
      rtl,
      range,
      className,
      selected,
      selectable,
      renderForMeasure,
      accessors,
      getters,
      components,
      getNow,
      renderHeader,
      onSelect,
      localizer,
      onSelectStart,
      onSelectEnd,
      onDoubleClick,
      onKeyPress,
      resourceId,
      longPressThreshold,
      isAllDay,
      resizable,
      showAllEvents,
    } = this.props;

    if (renderForMeasure) return this.renderDummy();

    const metrics = this.slotMetrics(this.props);
    const { levels, extra } = metrics;
    const ScrollableWeekComponent = showAllEvents
      ? ScrollableWeekWrapper
      : NoopWrapper;
    const WeekWrapper = components.weekWrapper;
    const eventRowProps = {
      selected,
      accessors,
      getters,
      localizer,
      components,
      onSelect,
      onDoubleClick,
      onKeyPress,
      resourceId,
      slotMetrics: metrics,
      resizable,
    };

    return (
      <div className={className} role="rowgroup" ref={this.containerRef}>
        <BackgroundCells
          localizer={localizer}
          date={date}
          getNow={getNow}
          rtl={rtl}
          range={range}
          selectable={selectable}
          container={this.getContainer}
          getters={getters}
          onSelectStart={onSelectStart}
          onSelectEnd={onSelectEnd}
          onSelectSlot={this.handleSelectSlot}
          components={components}
          longPressThreshold={longPressThreshold}
          resourceId={resourceId}
        />
        <div
          className={clsx(
            'rbc-row-content',
            showAllEvents && 'rbc-row-content-scrollable'
          )}
          role="row"
        >
          {renderHeader && (
            <div className="rbc-row" ref={this.headingRowRef}>
              {range.map(this.renderHeadingCell)}
            </div>
          )}
          <ScrollableWeekComponent>
            <WeekWrapper isAllDay={isAllDay} {...eventRowProps} rtl={rtl}>
              {levels.map((segs, idx) => (
                <GapAwareEventRow key={idx} segments={segs} {...eventRowProps} />
              ))}
              {!!extra.length && (
                <GapAwareEventEndingRow
                  segments={extra}
                  onShowMore={this.handleShowMore}
                  {...eventRowProps}
                />
              )}
            </WeekWrapper>
          </ScrollableWeekComponent>
        </div>
      </div>
    );
  }
}
